import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";

interface Category {
  id: number;
  name: string;
  icon: string;
  is_avl: number;
}
interface Location {
  id: number;
  name: string;
}
interface Campus {
  id: number;
  name: string;
}

interface FormData {
  campusId: number | null;
  categoryIds: number[];
  locationId: number | null;
  roomDetail: string;
  eventImpact: "" | "affecting_now" | "starting_soon" | "not_affecting" | "not_sure";
  priority: "" | "critical" | "high" | "medium" | "low";
  description: string;
  activityAtTime: string;
  workedBefore: "" | "yes" | "no" | "not_sure";
  lastWorkedAt: string;
  equipmentName: string;
  equipmentAssetTag: string;
  equipmentManufacturer: string;
  equipmentModel: string;
  equipmentLocation: string;
  reporterName: string;
  reporterEmail: string;
  reporterPhone: string;
  reporterContactOk: boolean;
}

const ACTIVITIES = [
  "Starting the service",
  "Rehearsal",
  "Playing music",
  "Using microphone",
  "Showing presentation",
  "Streaming",
  "Connecting computer",
  "Using Wi-Fi",
  "Printing",
  "Other",
];

const ACCEPTED_FILE_TYPES = "image/jpeg,image/png,image/heic,image/heif,application/pdf";
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const initialForm: FormData = {
  campusId: null,
  categoryIds: [],
  locationId: null,
  roomDetail: "",
  eventImpact: "",
  priority: "",
  description: "",
  activityAtTime: "",
  workedBefore: "",
  lastWorkedAt: "",
  equipmentName: "",
  equipmentAssetTag: "",
  equipmentManufacturer: "",
  equipmentModel: "",
  equipmentLocation: "",
  reporterName: "",
  reporterEmail: "",
  reporterPhone: "",
  reporterContactOk: true,
};

export function Report() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(initialForm);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [attachmentsEnabled, setAttachmentsEnabled] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [scannedEquipment, setScannedEquipment] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ campuses: Campus[]; categories: Category[]; locations: Location[]; attachmentsEnabled: boolean }>(
        "/meta"
      )
      .then((data) => {
        setCampuses(data.campuses);
        setCategories(data.categories);
        setLocations(data.locations);
        setAttachmentsEnabled(data.attachmentsEnabled);

        // Campus shortcuts (one per church location) link to /report?campus=<id>
        // so the desk/PC at that campus doesn't require members to pick it.
        const campusParam = searchParams.get("campus");
        const campusId = campusParam ? Number(campusParam) : null;
        if (campusId && data.campuses.some((c) => c.id === campusId)) {
          setForm((f) => ({ ...f, campusId }));
        }
      })
      .catch(() => setError("We couldn't load the form right now. Please refresh the page."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = searchParams.get("eq");
    if (!token) return;
    api
      .get<{
        equipment_type: string;
        asset_tag: string | null;
        manufacturer: string | null;
        model: string | null;
        location_id: number | null;
        location_name: string | null;
        campus_id: number | null;
        campus_name: string | null;
      }>(`/equipment/lookup/${encodeURIComponent(token)}`)
      .then((eq) => {
        setForm((f) => ({
          ...f,
          campusId: eq.campus_id ?? f.campusId,
          locationId: eq.location_id ?? f.locationId,
          equipmentName: eq.equipment_type,
          equipmentAssetTag: eq.asset_tag ?? "",
          equipmentManufacturer: eq.manufacturer ?? "",
          equipmentModel: eq.model ?? "",
        }));
        setScannedEquipment(`${eq.equipment_type}${eq.asset_tag ? ` (${eq.asset_tag})` : ""}`);
      })
      .catch(() => {
        // Equipment QR codes are a convenience, not a requirement — if the
        // lookup fails, the member can still fill out the form normally.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const TOTAL_STEPS = attachmentsEnabled ? 11 : 10;
  const PHOTO_STEP = 10;

  function onPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setPhotoError(null);
    if (file && file.size > MAX_FILE_BYTES) {
      setPhotoError("That file is too large. Please choose something under 10MB.");
      setPhoto(null);
      e.target.value = "";
      return;
    }
    setPhoto(file);
  }

  function update<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleCategory(id: number) {
    setForm((f) => ({
      ...f,
      categoryIds: f.categoryIds.includes(id) ? f.categoryIds.filter((c) => c !== id) : [...f.categoryIds, id],
    }));
  }

  const stepError = useMemo(() => validateStep(step, form, TOTAL_STEPS), [step, form, TOTAL_STEPS]);

  function next() {
    if (stepError) {
      setError(stepError);
      return;
    }
    setError(null);
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  }
  function back() {
    setError(null);
    setStep((s) => Math.max(1, s - 1));
  }

  async function submit() {
    if (stepError) {
      setError(stepError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.post<{
        incidentId: number;
        incidentNumber: string;
        priority: string;
        status: string;
        submittedAt: string;
        uploadToken: string | null;
      }>("/incidents", {
        campusId: form.campusId,
        categoryIds: form.categoryIds,
        locationId: form.locationId,
        roomDetail: form.roomDetail || null,
        eventImpact: form.eventImpact,
        priority: form.priority,
        description: form.description,
        activityAtTime: form.activityAtTime || null,
        workedBefore: form.workedBefore || null,
        lastWorkedAt: form.lastWorkedAt || null,
        equipmentName: form.equipmentName || null,
        equipmentAssetTag: form.equipmentAssetTag || null,
        equipmentManufacturer: form.equipmentManufacturer || null,
        equipmentModel: form.equipmentModel || null,
        equipmentLocation: form.equipmentLocation || null,
        reporterName: form.reporterName,
        reporterEmail: form.reporterEmail,
        reporterPhone: form.reporterPhone || null,
        reporterContactOk: form.reporterContactOk,
      });

      let photoAttached = false;
      let photoWarning: string | null = null;
      if (photo && result.uploadToken) {
        try {
          const fd = new FormData();
          fd.set("token", result.uploadToken);
          fd.set("file", photo);
          const res = await fetch(`/api/incidents/${result.incidentId}/attachments`, {
            method: "POST",
            body: fd,
            credentials: "same-origin",
          });
          if (res.ok) {
            photoAttached = true;
          } else {
            const data = await res.json().catch(() => null);
            photoWarning = data?.error ?? "Your report was submitted, but the photo couldn't be attached.";
          }
        } catch {
          photoWarning = "Your report was submitted, but the photo couldn't be attached.";
        }
      }

      navigate("/report/confirmation", { state: { ...result, photoAttached, photoWarning } });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "We couldn't submit your report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container">
      <div className="card">
        <h2>Report a Technology Problem</h2>
        {scannedEquipment && (
          <div className="info-banner">
            📷 Reporting a problem with: <strong>{scannedEquipment}</strong>
          </div>
        )}
        <div className="step-indicator" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={TOTAL_STEPS}>
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div key={i} className={`step-dot ${i + 1 === step ? "active" : i + 1 < step ? "done" : ""}`} />
          ))}
        </div>
        <p className="hint">
          Step {step} of {TOTAL_STEPS}
        </p>

        {error && (
          <div className="alert-banner" role="alert">
            {error}
          </div>
        )}

        {step === 1 && (
          <fieldset>
            <legend>What type of problem are you reporting? (choose all that apply)</legend>
            <div className="card-grid">
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="pick-card"
                  aria-pressed={form.categoryIds.includes(c.id)}
                  onClick={() => toggleCategory(c.id)}
                >
                  <span className="emoji" aria-hidden="true">
                    {c.icon}
                  </span>
                  {c.name}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {step === 2 && (
          <fieldset>
            <legend>Which campus are you at?</legend>
            <div className="card-grid">
              {campuses.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="pick-card"
                  aria-pressed={form.campusId === c.id}
                  onClick={() => update("campusId", c.id)}
                >
                  <span className="emoji" aria-hidden="true">
                    📍
                  </span>
                  {c.name}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {step === 3 && (
          <fieldset>
            <legend>Where is the problem?</legend>
            <div className="field">
              <label htmlFor="location">Room / area</label>
              <select id="location" value={form.locationId ?? ""} onChange={(e) => update("locationId", e.target.value ? Number(e.target.value) : null)}>
                <option value="">Choose a location…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="roomDetail">Room / more detail (optional)</label>
              <input id="roomDetail" type="text" value={form.roomDetail} onChange={(e) => update("roomDetail", e.target.value)} />
            </div>
          </fieldset>
        )}

        {step === 4 && (
          <fieldset>
            <legend>Is this affecting an event right now?</legend>
            <div className="severity-grid">
              {[
                ["affecting_now", "🔴", "YES — Worship/service/event is currently affected"],
                ["starting_soon", "🟠", "YES — Event starts soon"],
                ["not_affecting", "🟢", "NO — Not currently affecting an event"],
                ["not_sure", "❔", "Not sure"],
              ].map(([val, icon, label]) => (
                <button
                  key={val}
                  type="button"
                  className="severity-card"
                  aria-pressed={form.eventImpact === val}
                  onClick={() => update("eventImpact", val as FormData["eventImpact"])}
                >
                  <span className="severity-icon" aria-hidden="true">
                    {icon}
                  </span>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {step === 5 && (
          <fieldset>
            <legend>How serious is the problem?</legend>
            <div className="severity-grid">
              {[
                ["critical", "🔴", "Critical", "The problem is stopping or seriously affecting worship/service/event operations."],
                ["high", "🟠", "High", "Important equipment or technology is unavailable."],
                ["medium", "🟡", "Medium", "There is a problem, but a workaround exists."],
                ["low", "🟢", "Low", "General technology issue or request — not urgent."],
              ].map(([val, icon, label, desc]) => (
                <button
                  key={val}
                  type="button"
                  className="severity-card"
                  aria-pressed={form.priority === val}
                  onClick={() => update("priority", val as FormData["priority"])}
                >
                  <span className="severity-icon" aria-hidden="true">
                    {icon}
                  </span>
                  <span>
                    <strong>{label}</strong>
                    {desc}
                  </span>
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {step === 6 && (
          <fieldset>
            <legend>What happened?</legend>
            <div className="field">
              <label htmlFor="description">Please describe the problem in your own words</label>
              <textarea
                id="description"
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                placeholder="Example: The wireless microphone stopped working during rehearsal."
                maxLength={4000}
              />
            </div>
          </fieldset>
        )}

        {step === 7 && (
          <fieldset>
            <legend>What were you doing when it happened?</legend>
            <div className="card-grid">
              {ACTIVITIES.map((a) => (
                <button
                  key={a}
                  type="button"
                  className="pick-card"
                  aria-pressed={form.activityAtTime === a}
                  onClick={() => update("activityAtTime", a)}
                  style={{ minHeight: "auto", padding: "0.8rem 0.5rem" }}
                >
                  {a}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        {step === 8 && (
          <fieldset>
            <legend>Did it work before?</legend>
            <div className="tag-row">
              {(["yes", "no", "not_sure"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  className="pick-card"
                  style={{ minHeight: "auto", flex: "1 1 100px" }}
                  aria-pressed={form.workedBefore === v}
                  onClick={() => update("workedBefore", v)}
                >
                  {v === "yes" ? "Yes" : v === "no" ? "No" : "Not sure"}
                </button>
              ))}
            </div>
            {form.workedBefore === "yes" && (
              <div className="field" style={{ marginTop: "1rem" }}>
                <label htmlFor="lastWorkedAt">When did it last work correctly?</label>
                <input
                  id="lastWorkedAt"
                  type="datetime-local"
                  value={form.lastWorkedAt}
                  onChange={(e) => update("lastWorkedAt", e.target.value)}
                />
              </div>
            )}
          </fieldset>
        )}

        {step === 9 && (
          <fieldset>
            <legend>What equipment is affected? (optional)</legend>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="eqName">Equipment name</label>
                <input id="eqName" type="text" value={form.equipmentName} onChange={(e) => update("equipmentName", e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="eqTag">Asset/tag number (if known)</label>
                <input id="eqTag" type="text" value={form.equipmentAssetTag} onChange={(e) => update("equipmentAssetTag", e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="eqMake">Manufacturer</label>
                <input id="eqMake" type="text" value={form.equipmentManufacturer} onChange={(e) => update("equipmentManufacturer", e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="eqModel">Model</label>
                <input id="eqModel" type="text" value={form.equipmentModel} onChange={(e) => update("equipmentModel", e.target.value)} />
              </div>
            </div>
          </fieldset>
        )}

        {step === PHOTO_STEP && attachmentsEnabled && (
          <fieldset>
            <legend>Upload a picture (optional)</legend>
            <p className="hint">A picture can help the IT/AVL team troubleshoot the problem faster.</p>
            <div className="field">
              <label htmlFor="photo">Photo (JPG, PNG, HEIC, or PDF — up to 10MB)</label>
              <input id="photo" type="file" accept={ACCEPTED_FILE_TYPES} onChange={onPhotoChange} />
              {photo && <p className="hint">Selected: {photo.name}</p>}
              {photoError && (
                <p className="field-error" role="alert">
                  {photoError}
                </p>
              )}
            </div>
          </fieldset>
        )}

        {step === TOTAL_STEPS && (
          <fieldset>
            <legend>Your contact information</legend>
            <div className="field">
              <label htmlFor="name">Name</label>
              <input id="name" type="text" value={form.reporterName} onChange={(e) => update("reporterName", e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={form.reporterEmail} onChange={(e) => update("reporterEmail", e.target.value)} />
              <p className="hint">We'll use this to send you your incident number and let you check its status.</p>
            </div>
            <div className="field">
              <label htmlFor="phone">Phone (optional)</label>
              <input id="phone" type="tel" value={form.reporterPhone} onChange={(e) => update("reporterPhone", e.target.value)} />
            </div>
            <div className="field">
              <label style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontWeight: 400 }}>
                <input
                  type="checkbox"
                  style={{ width: "auto", minHeight: "auto", marginTop: "0.2rem" }}
                  checked={form.reporterContactOk}
                  onChange={(e) => update("reporterContactOk", e.target.checked)}
                />
                I would like the IT/AVL team to contact me about this incident.
              </label>
            </div>
          </fieldset>
        )}

        <div className="step-actions">
          {step > 1 ? (
            <button type="button" className="btn btn-secondary" onClick={back}>
              ← Back
            </button>
          ) : (
            <span />
          )}
          {step < TOTAL_STEPS ? (
            <button type="button" className="btn btn-primary" onClick={next}>
              Next →
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-lg" onClick={submit} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit Report"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function validateStep(step: number, form: FormData, totalSteps: number): string | null {
  if (step === totalSteps) {
    if (!form.reporterName.trim()) return "Please tell us your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.reporterEmail)) return "Please enter a valid email address.";
    return null;
  }
  switch (step) {
    case 1:
      return form.categoryIds.length === 0 ? "Please choose at least one problem type." : null;
    case 2:
      return form.campusId === null ? "Please choose which campus you're at." : null;
    case 3:
      return null;
    case 4:
      return form.eventImpact === "" ? "Please tell us if this is affecting an event." : null;
    case 5:
      return form.priority === "" ? "Please choose a severity level." : null;
    case 6:
      return form.description.trim().length === 0 ? "Please describe the problem." : null;
    default:
      return null;
  }
}
